import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Search, Eye, Download, Send, Copy, XCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft:             "bg-gray-100 text-gray-700",
  sent:              "bg-blue-100 text-blue-800",
  accepted:          "bg-green-100 text-green-800",
  changes_requested: "bg-yellow-100 text-yellow-800",
  expired:           "bg-orange-100 text-orange-800",
  cancelled:         "bg-red-100 text-red-700",
};

const STATUS_FILTERS = ["All", "draft", "sent", "accepted", "changes_requested", "expired", "cancelled"];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function isExpired(validUntil) {
  if (!validUntil) return false;
  try { return new Date(validUntil) < new Date(); }
  catch { return false; }
}

export default function UmrahQuotations() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [msg, setMsg] = useState(null);
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah-quotations`, { headers: getAuthHeaders() });
      setQuotations(r.data || []);
    } catch { /**/ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const filtered = quotations.filter(q => {
    if (statusFilter !== "All" && q.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (q.customer_name || "").toLowerCase().includes(s) ||
        (q.customer_email || "").toLowerCase().includes(s) ||
        (q.quotation_number || "").toLowerCase().includes(s) ||
        (q.package_reference || "").toLowerCase().includes(s) ||
        ((q.snapshot?.package_name) || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const openPreview = async (q) => {
    setActionLoading(`preview-${q.id}`);
    try {
      const r = await axios.get(`${API}/umrah-quotations/${q.id}/preview-pdf`, {
        responseType: "blob", headers: getAuthHeaders(),
      });
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPreviewUrl(url);
    } catch { showMsg("Failed to load preview.", "error"); }
    finally { setActionLoading(null); }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const downloadPdf = async (q) => {
    setActionLoading(`dl-${q.id}`);
    try {
      const r = await axios.get(`${API}/umrah-quotations/${q.id}/download-pdf`, {
        responseType: "blob", headers: getAuthHeaders(),
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quotation_number || q.id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { showMsg("Failed to download.", "error"); }
    finally { setActionLoading(null); }
  };

  const handleDuplicate = async (q) => {
    setActionLoading(`dup-${q.id}`);
    try {
      const r = await axios.post(`${API}/umrah-quotations/${q.id}/duplicate`, {}, { headers: getAuthHeaders() });
      showMsg(`Duplicated as ${r.data.quotation_number}`);
      await load();
    } catch { showMsg("Failed to duplicate.", "error"); }
    finally { setActionLoading(null); }
  };

  const handleCancel = async (q) => {
    if (!window.confirm(`Cancel quotation ${q.quotation_number}?`)) return;
    setActionLoading(`cancel-${q.id}`);
    try {
      await axios.patch(`${API}/umrah-quotations/${q.id}/status`, { status: "cancelled" }, { headers: getAuthHeaders() });
      await load();
    } catch { showMsg("Failed to cancel.", "error"); }
    finally { setActionLoading(null); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-5">

        {msg && (
          <div className={`rounded-lg px-4 py-3 text-sm font-semibold ${msg.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
            {msg.text}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Moon className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Umrah Quotations</h1>
            <p className="text-sm text-gray-500">{quotations.length} quotation{quotations.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
            {STATUS_FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition-colors ${
                  statusFilter === f ? "bg-white shadow text-amber-800" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {f.replace("_", " ")}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer, quotation #, package…"
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              {quotations.length === 0 ? "No quotations yet." : "No quotations match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Quotation #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Package</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Travel Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Travellers</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Valid Until</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(q => {
                    const snap = q.snapshot || {};
                    const expired = isExpired(q.valid_until);
                    return (
                      <tr key={q.id} className="hover:bg-amber-50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-amber-800 font-semibold whitespace-nowrap">
                          {q.quotation_number || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{q.customer_name || "—"}</div>
                          {q.customer_email && <div className="text-xs text-gray-400">{q.customer_email}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs max-w-[160px] truncate">
                          <div>{snap.package_name || "—"}</div>
                          <div className="font-mono text-gray-400">{q.package_reference}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                          {snap.flexible_dates
                            ? <span className="italic text-gray-400">Flexible</span>
                            : (snap.travel_start_date
                              ? `${fmtDate(snap.travel_start_date)} – ${fmtDate(snap.travel_end_date)}`
                              : "—")}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-center">
                          {q.number_of_travellers || "—"}
                        </td>
                        <td className="px-4 py-3 font-semibold text-amber-800 whitespace-nowrap">
                          €{(q.total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[q.status] || "bg-gray-100 text-gray-600"}`}>
                            {q.status?.replace("_", " ")}
                          </span>
                          {expired && q.status !== "expired" && q.status !== "cancelled" && (
                            <span className="ml-1 text-xs text-orange-600 font-semibold">EXPIRED</span>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-xs ${expired ? "text-orange-600 font-semibold" : "text-gray-500"}`}>
                          {fmtDate(q.valid_until) || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              title="Preview PDF"
                              disabled={!!actionLoading}
                              onClick={() => openPreview(q)}
                              className="p-1.5 text-gray-400 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors disabled:opacity-40"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              title="Download PDF"
                              disabled={!!actionLoading}
                              onClick={() => downloadPdf(q)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-40"
                            >
                              <Download size={14} />
                            </button>
                            <button
                              title="Duplicate"
                              disabled={!!actionLoading}
                              onClick={() => handleDuplicate(q)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors disabled:opacity-40"
                            >
                              <Copy size={14} />
                            </button>
                            {q.status !== "cancelled" && (
                              <button
                                title="Cancel"
                                disabled={!!actionLoading}
                                onClick={() => handleCancel(q)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-40"
                              >
                                <XCircle size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/60 flex flex-col z-50">
          <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-200 shrink-0">
            <span className="text-sm font-semibold text-gray-800">Umrah Quotation Preview</span>
            <button
              onClick={closePreview}
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
            >
              Close
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe src={previewUrl} title="Quotation Preview" className="w-full h-full border-0" />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
